import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import styled from "styled-components";
import { ListItem } from "./ListItem";
import OpenedListItem from "./OpenedListItem";
import { MenuToggle } from "./MenuToggle";
import Icon from "@mdi/react";
import {
  mdiCogs,
  mdiConsole,
  mdiContentCopy,
  mdiFolderSwapOutline,
  mdiLan,
  mdiQualityMedium,
  mdiText,
  mdiWeb,
} from "@mdi/js";
import NoteComponent from "./components/NoteComponent";
import TabletInterface from "./TabletInterface";
import DeviceSettings from "./DeviceSettings";
import { getCurrentSession } from "./utils/userManager";

const ContentContainer = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  justify-items: stretch;
  align-content: start;
  gap: 1vh;
  overflow-y: scroll;
  width: 100%;
  padding: 1vh 1vh 12vh 1vh;
`;
const TopBar = styled.div`
  height: 5vh;
  width: 100%;
  background-color: #fff;
  grid-column: 1/-1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1vh;
`;
const StyledMenu = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 4vh;
  font-weight: 600;
`;
const FullRowNoteComponent = styled(NoteComponent)`
  grid-column: 1 / -1;
  background-color: #fff;
`;

export const FacilityTopBar = ({
  itemIds,
  chosenFacility,
  isMobile,
  handleToggle,
  iconSize,
}) => {
  const session = getCurrentSession();
  const firstLane = itemIds?.[0];
  const isConnected = Boolean(session?.isAdmin);

  function getSelectedFacility() {
    return chosenFacility || itemIds?.[0]?.facility || "No facility selected";
  }

  function getFacilitySlug() {
    const facility = firstLane?.facility || chosenFacility;

    if (!facility) {
      return "";
    }

    return encodeURIComponent(
      facility.trim().toLowerCase().replace(/\s+/g, "-")
    );
  }

  const copyToClipboard = async (stringText) => {
    const textArea = document.createElement("textarea");
    textArea.value = stringText;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand("copy");
    } catch (err) { }
    document.body.removeChild(textArea);
  };

  return (
    <TopBar>
      {isMobile && (
        <StyledMenu>
          <MenuToggle toggle={handleToggle} />
        </StyledMenu>
      )}

      {!isMobile && (
        <h1 style={{ color: "#4a6283" }}>{getSelectedFacility()}</h1>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-evenly",
          gap: "1vh",
        }}
      >
        {isConnected && getSelectedFacility() !== "No facility selected" && (
          <>
            <Icon
              style={{ color: "#4a6283", cursor: "pointer" }}
              path={mdiContentCopy}
              size={iconSize}
              onClick={() => {
                copyToClipboard(firstLane?.status?.serverIP || "");
              }}
            />
            <Icon
              style={{ color: "#4a6283", cursor: "pointer" }}
              path={mdiQualityMedium}
              size={iconSize}
              onClick={() => {
                const hostname = firstLane?.status?.serverIP;
                if (!hostname) return; // or surface a toast/log
                window.chrome?.runtime?.sendMessage?.(
                  "gfbpmcbjdgehbjcbholepibdiipdifdd",
                  { type: "open-popout", hostname },
                  () => {
                    if (window.chrome.runtime.lastError) {
                      console.warn("MQTT extension unreachable:", window.chrome.runtime.lastError.message);
                    }
                  }
                );
              }}
            />
            <Icon
              style={{ color: "#4a6283", cursor: "pointer" }}
              path={mdiText}
              size={iconSize}
              onClick={() => {
                const facilitySlug = getFacilitySlug();
                if (!facilitySlug) return;

                window.open(
                  "https://stats.edab.online/facility/" + facilitySlug,
                  "_blank",
                  "noopener,noreferrer"
                );
              }}
            />
            <Icon
              style={{ color: "#4a6283", cursor: "pointer" }}
              path={mdiFolderSwapOutline}
              size={iconSize}
              onClick={() => {
                if (!firstLane?.status?.serverIP) return;
                window.location.assign(
                  "scp://edab:Fabriksgatan16@" + firstLane.status.serverIP
                );
              }}
            />
            <Icon
              style={{ color: "#4a6283", cursor: "pointer" }}
              path={mdiConsole}
              size={iconSize}
              onClick={() => {
                if (!firstLane?.status?.serverIP) return;
                window.location.assign("ssh://edab@" + firstLane.status.serverIP);
              }}
            />
            <Icon
              style={{ color: "#4a6283", cursor: "pointer" }}
              path={mdiLan}
              size={iconSize}
              onClick={() => {
                if (!firstLane?.status?.serverIP) return;
                window.open(
                  "https://" + firstLane.status.serverIP + ":8443",
                  "_blank",
                  "noopener,noreferrer"
                );
              }}
            />
            <Icon
              style={{ color: "#4a6283", cursor: "pointer" }}
              path={mdiWeb}
              size={iconSize}
              onClick={() => {
                if (!firstLane?.status?.serverIP) return;
                window.open(
                  "http://" + firstLane.status.serverIP + ":3000",
                  "_blank",
                  "noopener,noreferrer"
                );
              }}
            />
            <Icon
              style={{ color: "#4a6283", cursor: "pointer" }}
              path={mdiCogs}
              size={iconSize}
              onClick={() => {
                if (!firstLane?.status?.serverIP) return;
                window.open(
                  `http://10.1.1.188:3100/?lane=${firstLane.lane}&server=${firstLane.status.serverIP}`,
                  "_blank",
                  "noopener,noreferrer"
                );
              }}
            />
          </>
        )}
      </div>
    </TopBar>
  );
};

const Content = ({
  itemIds,
  chosenFacility,
  style,
  isMobile,
  handleToggle,
  iconSize,
  showTopBar = true,
}) => {
  const [lanes, setLanes] = useState(itemIds);
  const [selectedId, setSelectedId] = useState(null);
  const [tablet, setTablet] = useState(null);
  const [deviceSettings, setDeviceSettings] = useState(null);
  const session = getCurrentSession();
  const firstLane = lanes?.[0];

  useEffect(() => {
    setLanes(itemIds);
  }, [itemIds]);

  return (
    <ContentContainer style={style}>
      {showTopBar && (
        <FacilityTopBar
          itemIds={lanes}
          chosenFacility={chosenFacility}
          isMobile={isMobile}
          handleToggle={handleToggle}
          iconSize={iconSize}
        />
      )}

      {(lanes || [])
        .filter((item) => item.status && item.batteryHistory)
        .map((item, i) => (
          <ListItem
            key={i}
            i={item}
            canRefreshService={Boolean(session?.isAdmin || session?.canRefreshService)}
            onClickTablet={(Item) => {
              setTablet(item);
            }}
            onClick={() => {
              setSelectedId(item.id);
            }}
          />
        ))}
      {chosenFacility && (
        <FullRowNoteComponent
          style={{ backgroundColor: "#fff", gridColumn: "1 / -1" }}
          facility={chosenFacility}
          lane={100}
        />
      )}
      {selectedId && (
        <OpenedListItem
          setDeviceSettings={setDeviceSettings}
          item={lanes.find((i) => i.id === selectedId)}
          onClick={() => setSelectedId(null)}
        />
      )}
      {tablet && (
        <TabletInterface
          admin={Boolean(session?.isAdmin)}
          item={tablet}
          onClick={() => {
            setTablet(null);
          }}
        />
      )}
      {deviceSettings && (
        <DeviceSettings
          lane={deviceSettings.lane}
          server={firstLane?.status?.serverIP}
          setDeviceSettings={setDeviceSettings}
        />
      )}
    </ContentContainer>
  );
};

export default Content;
